/**
 * Transport contract every domain factory expects.
 *
 * Deliberately minimal: this package knows nothing of the base URL, the token
 * or offline handling — the application supplies them. A domain test only needs
 * an object literal of this shape.
 */
export interface HttpClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  /**
   * Warning: **no route uses this, and none should.** The API whitelists its
   * CORS methods (`main.ts`, `enableCors`) and `PUT` is not among them, so a
   * browser's preflight passes and the request itself fails with
   * `net::ERR_FAILED` — while every server-side test stays green, because Node
   * does not enforce CORS. Use `patch` for a full replacement, as
   * `/wishlists/reorder` and `/notifications/preferences` do.
   */
  put<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
}

/**
 * A `multipart/form-data` body, built by the application.
 *
 * Warning: Described by its shape rather than typed `FormData`: this package declares
 * no ambient lib, so the DOM type does not exist here. A real `FormData`
 * satisfies it, and the transport recognises that case to skip JSON
 * serialisation.
 */
export interface MultipartBody {
  append(name: string, value: unknown, fileName?: string): void;
}

/**
 * Encodes a value meant for a single URL segment.
 *
 * @param value Raw value, typically an id or a token.
 * @returns The percent-encoded segment.
 */
export const segment = (value: string): string => encodeURIComponent(value);

/**
 * Builds a query string, skipping absent values.
 *
 * Warning: Hand-written rather than using `URLSearchParams`: this package declares no
 * ambient lib (neither `DOM` nor `node`) so that it depends on no runtime.
 * Adding `DOM` just for this would bring `window` and `document` in with it.
 *
 * @param params Parameters to serialise; `undefined` values are dropped.
 * @returns A string starting with `?`, or an empty string when nothing is left.
 */
export function query(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const pairs = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );

  return pairs.length ? `?${pairs.join('&')}` : '';
}
